import { useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

export default function FormSearchSelect({
  name,
  value,
  onChange,
  disabled,
  required,
  placeholder,
  options,
  resetKey,
  searchable = true
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery('');
  }, [value, disabled, resetKey]);

  const filtered = !searchable || query === ''
    ? options
    : options.filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase())
      );

  const selected = options.find((option) => String(option.id) === String(value));

  return (
    <div className="relative">
      {required ? (
        <input
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          name={name}
          value={value || ''}
          required
          onChange={() => {}}
        />
      ) : null}
      <Combobox
        value={value || ''}
        onChange={(next) => {
          setQuery('');
          onChange(next);
        }}
        disabled={disabled}
        immediate
      >
        <div className="relative mt-1">
          <div
            className={`relative w-full cursor-default overflow-hidden rounded-lg text-left border border-gray-300 focus-within:outline-none ${
              disabled ? 'bg-gray-100' : 'bg-white'
            }`}
          >
            <Combobox.Input
              className="w-full border-none bg-transparent py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 disabled:text-gray-500"
              onChange={(event) => {
                if (searchable) setQuery(event.target.value);
              }}
              displayValue={() => selected?.label || ''}
              placeholder={placeholder}
              readOnly={!searchable}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
            {filtered.length === 0 ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Nothing found.
              </div>
            ) : (
              filtered.map((option) => (
                <Combobox.Option
                  key={option.id}
                  value={option.id}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected: isSelected, active }) => (
                    <>
                      <span className={`flex items-center justify-between gap-3 ${isSelected ? 'font-medium' : 'font-normal'}`}>
                        <span className="truncate">{option.label}</span>
                        {option.meta ? (
                          <span className={`shrink-0 text-xs ${active ? 'text-gray-600' : 'text-gray-500'}`}>
                            {option.meta}
                          </span>
                        ) : null}
                      </span>
                      {isSelected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-gray-900' : 'text-slate-600'
                          }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </div>
      </Combobox>
    </div>
  );
}

function countLabel(count, singular) {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

export function optionMetaCount(count, singular) {
  return countLabel(count || 0, singular);
}
